import { JourneyStep } from '@/types'
import { ActionContext, Module } from 'vuex'
import { RootState } from '..'
import cms from '@/cms'

interface JourneyState {
  steps: JourneyStep[],
  currentPosition: number
}

export const defaultSteps: JourneyStep[] = [
  { position: 0, name: 'Home' },
  { position: 2, name: 'Login' },
  { position: 1, name: 'Newsletter' },
  { position: 3, name: 'Playlist' },
  { position: 5, name: 'Result' }
]

export const state: JourneyState = {
  steps: defaultSteps,
  currentPosition: 0
}

const mutations = {
  SET_STEP (state: JourneyState, step: JourneyStep) {
    state.currentPosition = step.position
  },
  SET_JOURNEY_STEPS (state: JourneyState, steps: JourneyStep[]) {
    state.steps = steps
  }
}

const goToStep = (ctx: ActionContext<JourneyState, RootState>, position: number) => {
  const step = ctx.state.steps[position]
  const storageKey = `${cms.campaign.app}:${cms.campaign.id}:journeyPosition`
  if (step) {
    console.log('Setting step in storage and store', JSON.stringify(step))
    sessionStorage.setItem(storageKey, step.position.toString())
    ctx.commit('SET_STEP', step)
  }
}

const actions = {
  LOAD_JOURNEY_STATE (ctx: ActionContext<JourneyState, RootState>) {
    const storageKey = `${cms.campaign.app}:${cms.campaign.id}:journeyPosition`
    const position = sessionStorage.getItem(storageKey)
    if (position) {
      const step = ctx.state.steps[parseInt(position)]
      console.log('Step from session storage', JSON.stringify(step))
      if (step) {
        ctx.commit('SET_STEP', step)
      }
    }
  },
  GO_TO_STEP_NAME (ctx: ActionContext<JourneyState, RootState>, name: string) {
    // Set the current step to the first step matching the given name
    // If no match we go back to the first step
    // This is to ensure the current step matches the given URL and should
    // be called from views corresponding to steps
    const step = ctx.state.steps.find(step => step.name === name)
    console.log('Found step by name', JSON.stringify(step))
    if (step) {
      goToStep(ctx, step.position)
    } else {
      goToStep(ctx, 0)
    }
  },
  ENSURE_STEP_NAME (ctx: ActionContext<JourneyState, RootState>, name: string) {
    const currentStep = ctx.getters.currentStep
    if (currentStep && currentStep.name === name) {
      // Do nothing, we're already on desired step
      return
    }
    if (!currentStep) {
      console.log('There is no current step')
      // Find the first step with the given name
      const step = ctx.state.steps.find(step => step.name === name)
      if (step) {
        goToStep(ctx, step.position)
      } else { // Fall back to first step
        goToStep(ctx, 0)
      }
      return
    }
    // Find the most recent step below the current step with the given name
    // Iterate over the steps in reverse
    for (let i = currentStep.position - 1; i >= 0; i--) {
      const step = ctx.state.steps[i]
      console.log('Iterate', step.name, i)
      if (step.name === name) {
        goToStep(ctx, step.position)
        return
      }
    }
    // Fall back to first step
    console.log('Fall back to step 0')
    goToStep(ctx, 0)
  },
  GO_TO_NEXT_STEP (ctx: ActionContext<JourneyState, RootState>) {
    console.log('Go to next step after', ctx.state.currentPosition)
    goToStep(ctx, ctx.state.currentPosition + 1)
  },
  GO_TO_FIRST_STEP (ctx: ActionContext<JourneyState, RootState>) {
    console.log('Reset to first step')
    goToStep(ctx, 0)
  },
  RESTART_JOURNEY (ctx: ActionContext<JourneyState, RootState>) {
    console.log('Restart journey')
    ctx.dispatch('RESET_TRACK_SELECTION')
    goToStep(ctx, 0)
  }
}

const getters = {
  currentStep: (state: JourneyState) => {
    return state.steps[state.currentPosition] || null
  },
  journeySteps: (state: JourneyState) => {
    return state.steps
  }
}

export const journeyModule: Module<JourneyState, RootState> = {
  state: state,
  actions,
  getters,
  mutations
}
